/**
 * ReelsListPage
 *
 * Displays all reels with status badges (Draft / Ready to publish).
 * Includes dropdown filter to focus on specific status.
 */

import { useState, useMemo, useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { PostWriterSubmenuMemo } from '../components/post-writer/PostWriterSubmenu';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { CreateReelModal } from '../components/reels/CreateReelModal';
import {
  Film,
  Clock,
  AlertCircle,
  Loader2,
  Layers,
  Plus,
  MoreHorizontal,
  Trash2,
  CheckCircle2,
} from 'lucide-react';
import type { Id } from '../../convex/_generated/dataModel';

interface IReelItem {
  _id: Id<'reels'>;
  _creationTime: number;
  hook: string;
  status: string;
  brandName: string;
  businessCategory: string;
  generatedAt: number;
  completedAt?: number;
  hasSplitScenes?: boolean;
  totalScenes?: number;
  sceneSplitStatus?: string;
  isReady: boolean;
}

type StatusFilter = 'all' | 'draft' | 'ready';

export const ReelsListPage = () => {
  const navigate = useNavigate();
  // Omitting isReady returns all reels with isReady field
  const reels = useQuery(api.reels.getReelsByReadyStatus, {}) as IReelItem[] | undefined;
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [reelToDelete, setReelToDelete] = useState<Id<'reels'> | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteReel = useMutation(api.reels.deleteReel);

  const handleDelete = useCallback(async () => {
    if (!reelToDelete) return;
    setIsDeleting(true);
    try {
      await deleteReel({ reelId: reelToDelete });
    } finally {
      setIsDeleting(false);
      setReelToDelete(null);
    }
  }, [reelToDelete, deleteReel]);

  const isLoading = reels === undefined;
  const isEmpty = reels?.length === 0;

  // Filter by status
  const filteredReels = useMemo(() => {
    if (!reels) return [];
    if (statusFilter === 'all') return reels;
    if (statusFilter === 'ready') return reels.filter(r => r.isReady);
    return reels.filter(r => !r.isReady);
  }, [reels, statusFilter]);

  const handleReelClick = useCallback(
    (reelId: Id<'reels'>) => {
      navigate(`/reel/${reelId}`);
    },
    [navigate]
  );

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDisplayStatus = (reel: IReelItem): { label: string; icon: ReactNode } => {
    if (reel.status === 'failed') {
      return { label: 'Failed', icon: <AlertCircle className="h-4 w-4 text-red-500" /> };
    }
    if (
      reel.status === 'generating' ||
      reel.status === 'pending' ||
      reel.status === 'researching'
    ) {
      return {
        label: 'Generating',
        icon: <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />,
      };
    }
    if (reel.sceneSplitStatus === 'splitting') {
      return {
        label: 'Splitting scenes',
        icon: <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />,
      };
    }
    if (reel.hasSplitScenes && reel.isReady) {
      return { label: 'Ready', icon: <CheckCircle2 className="h-4 w-4 text-green-500" /> };
    }
    if (reel.hasSplitScenes) {
      return { label: 'In production', icon: <Film className="h-4 w-4 text-blue-500" /> };
    }
    return { label: 'Script ready', icon: <Clock className="h-4 w-4 text-foreground-muted" /> };
  };

  return {
    submenu: <PostWriterSubmenuMemo />,
    mainContent: (
      <div className="h-full bg-background">
        <div className="p-6">
          {/* Header with Status Filter */}
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Reels</h1>
              <p className="text-sm text-foreground-muted mt-1">All your generated reels</p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => setCreateModalOpen(true)}>
                <Plus className="mr-1.5 h-4 w-4" />
                New Reel
              </Button>
              <Select
                value={statusFilter}
                onValueChange={(value: StatusFilter) => setStatusFilter(value)}
              >
                <SelectTrigger size="sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="ready">Ready to publish</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <CreateReelModal open={createModalOpen} onOpenChange={setCreateModalOpen} />

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
            </div>
          )}

          {/* Empty State */}
          {!isLoading && isEmpty && (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Film className="h-12 w-12 text-foreground-muted mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">No reels yet</h3>
              <p className="text-sm text-foreground-muted">
                Generate a reel from a topic to see it here.
              </p>
            </div>
          )}

          {/* No Results After Filter */}
          {!isLoading && !isEmpty && filteredReels.length === 0 && (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Film className="h-12 w-12 text-foreground-muted mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">No matching reels</h3>
              <p className="text-sm text-foreground-muted">Try changing the status filter.</p>
            </div>
          )}

          {/* Reels List */}
          {!isLoading && filteredReels.length > 0 && (
            <div className="space-y-3">
              {filteredReels.map(reel => {
                const displayStatus = getDisplayStatus(reel);
                return (
                  <div
                    key={reel._id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleReelClick(reel._id)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleReelClick(reel._id);
                      }
                    }}
                    className="p-4 bg-background-card rounded-lg border border-border hover:border-border-hover cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        {/* Hook Preview */}
                        <div className="flex items-center gap-2.5">
                          <h3 className="text-sm font-medium text-foreground truncate">
                            {reel.hook?.slice(0, 60) || 'Untitled Reel'}
                            {reel.hook && reel.hook.length > 60 ? '...' : ''}
                          </h3>

                          {/* Ready Status Badge */}
                          {reel.isReady ? (
                            <Badge className="shrink-0 border-green-500/20 bg-green-500/10 text-green-600 dark:text-green-400">
                              Ready to publish
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="shrink-0">
                              Draft
                            </Badge>
                          )}
                        </div>

                        {/* Meta Info */}
                        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-muted">
                          <span>{reel.brandName}</span>
                          <span>&middot;</span>
                          <span>{formatDate(reel.generatedAt)}</span>
                          {reel.hasSplitScenes && reel.totalScenes && (
                            <>
                              <span>&middot;</span>
                              <span className="flex items-center gap-1">
                                <Layers className="h-3 w-3" />
                                {reel.totalScenes} scenes
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Status + Actions */}
                      <div className="flex items-center gap-1.5 ml-4">
                        {displayStatus.icon}
                        <span className="text-xs text-foreground-muted">{displayStatus.label}</span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 ml-1"
                              onClick={e => e.stopPropagation()}
                              onKeyDown={e => e.stopPropagation()}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={e => {
                                e.stopPropagation();
                                setReelToDelete(reel._id);
                              }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={reelToDelete !== null}
          onOpenChange={open => {
            if (!open) setReelToDelete(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete reel?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the reel and all its scenes, images, audio, and videos.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {isDeleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    ),
  };
};
